import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  Injector,
  TemplateRef,
  HostBinding,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  AfterViewInit,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { AbstractValueAccessor, MakeProvider } from '../abstract-value-accessor';
import { BehaviorSubject } from 'rxjs';
import { Placement } from '@ng-bootstrap/ng-bootstrap';

interface HighlightNode { type: 'term' | 'text'; value: string; }

@Component({
  selector: 'app-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss'],
  providers: [MakeProvider(TextFieldComponent)],
})
export class TextFieldComponent extends AbstractValueAccessor implements AfterViewInit, OnInit, OnChanges {
  @HostBinding('class.disabled')
  private get disabledClass(): boolean {
    return this.isDisabled;
  }

  @ViewChild('textfield')
  textfieldElement: ElementRef<HTMLInputElement>;

  @ViewChild('prefixRef')
  prefixRef: ElementRef<HTMLSpanElement>;

  textfieldElement$ = new BehaviorSubject<ElementRef<HTMLInputElement>>(null);

  constructor(private cd: ChangeDetectorRef, injector: Injector) {
    super(injector);
  }

  get isClearButtonDisplayed(): boolean {
    return this.clearButtonEnabled && this.value;
  }

  get isPlaceholderDisplayed(): boolean {
    return this.placeholder && !(this.isActive || this.hasValue);
  }

  @HostBinding('class.active')
  isActive: boolean;

  @Input()
  maxLength: number;

  @Input()
  minValue: number;

  @Input()
  maxValue: number;

  @Input()
  inputType = 'text';

  @Input()
  inputMode = 'text';

  @Input()
  errorTexts: { [key: string]: string | ((value?: any) => string) | TemplateRef<any> };

  @Input()
  label: string;

  @Input()
  sublabel: string;

  @Input()
  labelIcon: string;

  @Input()
  labelParamsObj: any;

  @Input()
  required: boolean;

  @Input()
  placeholder: string | TemplateRef<any>;

  @Input()
  placeholderIcon: string;

  @Input()
  placeholderParamsObj: any;

  @Input()
  displayTextTooltip = false;

  @Input()
  displayCharactersCounter: boolean;

  @Input()
  isCharactersCounterAbsolutePositioned = false;

  @Input()
  isControlErrorAbsolutePositioned = false;

  @Input()
  index: number;

  @Input()
  removable: boolean;

  @Input()
  showHideText: boolean;

  @Input()
  readonly: boolean;

  @Input()
  clearButtonEnabled: boolean;

  @Input()
  termsToHighlight: string[];

  @Input()
  displayErrorsAsTooltip: boolean;

  @Input()
  descriptionTooltipText: string;

  @Input()
  infoTooltip: string | TemplateRef<any>;

  @Input()
  infoTooltipPlacement: Placement;

  @Input()
  valuePrefix: string;

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-native
  input = new EventEmitter<InputEvent | Event>();

  @Output()
  valueChanges = new EventEmitter<string>();

  @Output()
  removeControl = new EventEmitter<string>();

  @Output()
  inputFocusOut = new EventEmitter();

  @Output()
  inputFocusIn = new EventEmitter();

  @Output()
  keyDown = new EventEmitter<KeyboardEvent>();

  // Autocomplete related properties

  @Input()
  suggestions: Array<string>;

  @Input()
  suggestionsConfig: { maxItemsToDisplay: number };

  @Input()
  addonText: string;

  @Input()
  autoFocus: boolean;

  @Input()
  labelWithColon = true;

  @Input()
  disableAutocomplete = false;

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    super.ngOnChanges(simpleChanges);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // Temporary workaround
      this.textfieldElement$.next(this.textfieldElement);

      if (this.autoFocus) {
        this.focus();
      }

      if (this.prefixRef?.nativeElement) {
        const defaultInputPaddingLeft = parseFloat((getComputedStyle(this.textfieldElement.nativeElement).paddingLeft));

        this.textfieldElement.nativeElement.style.paddingLeft = defaultInputPaddingLeft + this.prefixRef.nativeElement.offsetWidth + 'px';
        this.prefixRef.nativeElement.style.paddingLeft = defaultInputPaddingLeft + 'px';
      }
    }, 0);
  }

  focus(): void {
    this.textfieldElement.nativeElement.focus();
  }

  toggleShowHide(): void {
    const element = this.textfieldElement.nativeElement;
    if (element.type === 'password') {
      element.type = 'text';
    } else {
      element.type = 'password';
    }
  }

  clear(): void {
    this.textfieldElement.nativeElement.value = '';
    this.modelChange(this.textfieldElement.nativeElement.value);
  }

  inputBlur(): void {
    this.markAsDirty();
  }

  handleInputFocusIn(): void {
    this.isActive = true;
    this.inputFocusIn.emit();
    this.cd.detectChanges();
  }

  handleInputFocusOut(): void {
    if (this.textfieldElement.nativeElement.value) {
      this.textfieldElement.nativeElement.value = this.textfieldElement.nativeElement.value.trim();
      this.value = this.textfieldElement.nativeElement.value;
    }

    this.isActive = false;
    this.cd.detectChanges();
    this.inputFocusOut.emit();
  }

  getHighlightedSyntax(): HighlightNode[] {
    if (!this.value || !this.termsToHighlight?.length) {
      return null;
    }

    let text: string = this.value;
    text = text.replace(/\s/g, '\u00A0');
    const terms = this.termsToHighlight.map((term) => term.replace(/\s/g, '\u00A0'));
    const termsSet = new Set(terms);

    terms.forEach((term) => {
      text = text.replace(new RegExp(term, 'g'), `%%${term}%%`);
    });

    let hasTerms = false;

    const nodes = text?.split('%%').map((textPart) => {
      if (termsSet.has(textPart)) {
        hasTerms = true;
        return { type: 'term', value: textPart };
      } else {
        return { type: 'text', value: textPart };
      }
    });

    if (hasTerms) {
      return nodes as HighlightNode[];
    }

    return null;
  }

  blur(): void {
    this.textfieldElement.nativeElement.blur();
  }

  protected modelChange(text: string): void {
    if (text !== this.value) {
      this.value = text;
      this.valueChanges.emit(text);
    }
  }
}
